import { loaded } from '@ck/lib.app-config';
import { branch } from '@ck/lib.ui-branch';

/**
 * Possible backend environments
 */
export enum BackEnvs {
  Loading = 'Loading',
  Production = 'Production',
  Office = 'Office',
  Sandbox = 'Sandbox',
  Janeway = 'Janeway',
  Mocked = 'Local Development',
  Agent = 'Local Dashboard', // agent offline
  AgentProxy = 'Local Proxy', // agent online
  Unknown = 'Unknown',
}

/**
 * Current backend environment
 */
export let backEnv: BackEnvs = process.env.NODE_ENV === 'test' ? BackEnvs.Mocked : BackEnvs.Loading;

loaded.then((config) => {
  if (process.env.NODE_ENV === 'test') return;

  switch (config?.mode) {
    case 'agent':
      backEnv = BackEnvs.Agent;
      break;
    case 'agent-proxy':
      backEnv = BackEnvs.AgentProxy;
      break;
    default:
      backEnv =
        {
          'atlassandbox.io': BackEnvs.Sandbox,
          'atlasjaneway.io': BackEnvs.Janeway,
          'atlasoffice.io': BackEnvs.Office,
          'atlaslive.io': BackEnvs.Production,
          // HACK: for Cypress happiness .. should just pass this via env or something!
          localhost: BackEnvs.Sandbox,
        }[location.hostname] || BackEnvs.Unknown;
  }
});

/**
 * Possible frontend environments
 */
export enum FrontEnvs {
  Production,
  FeatureBranch,
  Dev,
}

/**
 * Current frontend environment
 */
export const frontEnv: FrontEnvs = (() => {
  if (branch === 'local' || location.hostname === 'localhost') return FrontEnvs.Dev;
  if (branch === 'main' || !branch) return FrontEnvs.Production;
  return FrontEnvs.FeatureBranch;
})();

/**
 * TODO: rethink the existence of this puppy.
 *   - replace this with a bunch of behavioral query methods and stop exporting backEnv entirely?
 *   - remove the passed-in param and let people just import this without importing backEnv?
 *   - get rid of this, given how many other callers just check backEnv themselves?!?
 */
export const getIsLocalOrMocked = (backEnv: BackEnvs) =>
  [BackEnvs.Agent, BackEnvs.AgentProxy, BackEnvs.Mocked].includes(backEnv);

/** Same as using "backEnv", but this as a function is easier to mock for tests. */
export const getEnvironment = () => backEnv;

/**
 * Additional helper functions to check the current environment.

/**
 * Checks if the current environment is the local dashboard.
 * @returns {boolean} True if the current environment is the local dashboard, false otherwise.
 */
export function isLocalDashboard(): boolean {
  return backEnv === BackEnvs.Agent;
}

/**
 * Checks if the current environment is production.
 * @returns {boolean} True if the current environment is production, false otherwise.
 */
export function isProduction(): boolean {
  return backEnv === BackEnvs.Production;
}

/**
 * Checks if the current environment is office.
 * @returns {boolean} True if the current environment is production, false otherwise.
 */
export function isOffice(): boolean {
  return backEnv === BackEnvs.Office;
}

/**
 * Checks if the code is running in Cypress.
 * @returns {boolean} True if the code is running in Cypress, false otherwise.
 */
export function isRunningInCypress(): boolean {
  return typeof window !== 'undefined' && (window as any).Cypress != null;
}
